import clsx from 'clsx';
import React, { useRef } from 'react';

import type { RatingColumnDefinition } from '@gitbook/doc-core';
import { createPalette, PaletteCommandType, type PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { AtomButton } from '@gitbook/spine-react';

import type {
    RecordValueRenderProps,
    TableDefinitionCommandsParams,
    TableDefinitionRenderSpec,
} from './types';

export const ratingDefinitionSpec: TableDefinitionRenderSpec<
    number | null,
    RatingColumnDefinition
> = {
    title: 'Rating',
    description: '',
    icon: Icon.ColumnTypeRating,
    ValueComponent: RecordRatingValue,
    commands: computeRatingColumnCommands,
    onKeyDown: (key, _value, setValue) => {
        switch (key) {
            case 'backspace':
                setValue(0);
                break;
        }
    },
};

function computeRatingColumnCommands(
    params: TableDefinitionCommandsParams<RatingColumnDefinition>
): PaletteGroupDef[] {
    const { definition, setDefinition } = params;

    return [
        {
            key: 'rating',
            commands: [
                {
                    key: 'rating.max_value',
                    title: 'Max',
                    icon: Icon.MaximumValue,
                    descendant: () =>
                        createPalette({
                            search: { placeholder: 'Select max rating…' },
                            groups: [
                                {
                                    key: 'max_values',
                                    commands: [...Array(5).keys()].map((value) => {
                                        return {
                                            key: `${value + 1}`,
                                            title: `${value + 1}`,
                                            type: PaletteCommandType.CHECKBOX,
                                            selected: definition.max === value + 1,
                                            trigger: () => {
                                                setDefinition({
                                                    ...definition,
                                                    max: value + 1,
                                                });
                                            },
                                        };
                                    }),
                                },
                            ],
                        }),
                },
            ],
        },
    ];
}

/** Component to render a value in the record. */
function RecordRatingValue(
    props: RecordValueRenderProps<number | null, RatingColumnDefinition>
): React.ReactElement {
    const { value, definition, definitionColorKind, onValueChange, readOnly, onKeyDown, selected } =
        props;
    const [hovered, setHovered] = React.useState(0);
    const ref = useRef<HTMLButtonElement>(null);

    const range = [...Array(definition.max + 1).keys()].slice(1);

    // If the current cell is `selected`, focus Rating button
    React.useEffect(() => {
        return selected ? ref.current?.focus() : ref.current?.blur();
    }, [selected]);

    const onPressRating = async (key: number) => {
        await onValueChange(key === value ? null : key);
        // Re-focus the button, otherwise focus is lost
        ref.current?.focus();
    };

    const onHoverRating = (key: number) => {
        if (!readOnly) {
            setHovered(key);
        }
    };

    // Enable number-keys to select the scores, any non-number keys are handled as normal through the table keyboard plugins.
    const interceptKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        const numericKey = Number.parseInt(event.key, 10);

        if (!Number.isNaN(numericKey) && numericKey >= 1 && numericKey <= definition.max) {
            return onPressRating(numericKey);
        }

        if (event.key === 'Delete' || event.key === 'Backspace') {
            return onPressRating(0);
        }

        // Pass other key events to parent handler if provided
        if (onKeyDown) {
            return onKeyDown(event);
        }
    };

    return (
        <div className="flex overflow-hidden">
            {range.map((key) => (
                <AtomButton
                    ref={ref}
                    key={`${value}-${key}`}
                    type="button"
                    onKeyDown={(event) => {
                        event.preventDefault();
                        interceptKeyDown(event);
                    }}
                    onMouseEnter={() => onHoverRating(key)}
                    onMouseLeave={() => onHoverRating(0)}
                    onClick={() => onPressRating(key)}
                    className={clsx(
                        'min-h-4 min-w-4 pr-0.5',
                        readOnly ? 'cursor-default' : 'cursor-pointer'
                    )}
                >
                    <span
                        className={clsx(
                            'select-none text-content-paragraph transition-all',
                            (hovered === 0 && value && value >= key) || hovered >= key
                                ? `text-${definitionColorKind}`
                                : 'text-inverted'
                        )}
                    >
                        ★
                    </span>
                </AtomButton>
            ))}
        </div>
    );
}
